import { useEffect, useMemo, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import Slider from 'react-slick'
import SafeImage from '../SafeImage'
import './index.scss'

export default function HomeBannerCache({ promoBanners = [], loading = false, maxDots = 3, navigationState }) {
  const history = useHistory()
  const sliderRef = useRef(null)
  const items = useMemo(() => (Array.isArray(promoBanners) ? promoBanners.slice(0, maxDots) : []), [promoBanners, maxDots])
  const showSkeleton = Boolean(loading)
  const sliderItems = items
  const slideCount = sliderItems.length

  useEffect(() => {
    if (showSkeleton || !sliderRef.current) {
      return
    }

    sliderRef.current.slickGoTo(0, true)
  }, [showSkeleton, slideCount])

  const sliderSettings = useMemo(
    () => ({
      arrows: false,
      dots: true,
      infinite: slideCount > 1,
      speed: 480,
      cssEase: 'cubic-bezier(0.22, 1, 0.36, 1)',
      slidesToShow: 1,
      slidesToScroll: 1,
      swipe: slideCount > 1,
      draggable: slideCount > 1,
      touchMove: slideCount > 1,
      swipeToSlide: true,
      touchThreshold: 10,
      waitForAnimate: false,
      accessibility: false,
      autoplay: slideCount > 1,
      autoplaySpeed: 5000,
      pauseOnHover: false,
      pauseOnFocus: false,
      pauseOnDotsHover: false,
      customPaging: () => <span className="HomeBannerCache-dot-inner" />,
      appendDots: (dots) => <div className="HomeBannerCache-dots">{dots}</div>
    }),
    [slideCount]
  )

  if (!showSkeleton && slideCount === 0) {
    return null
  }

  const renderBannerContent = (banner, index) => {
    const bannerUrl = String(banner?.bannerUrl || '').trim()
    const clickable = Boolean(bannerUrl)
    const isExternalHttps = /^https:\/\//i.test(bannerUrl)
    const isInternalPath = bannerUrl.startsWith('/')
    const key = banner?.systemPromoBannersId ?? `${banner?.bannerImageUrl || 'banner'}-${index}`
    const className = `HomeBannerCache-item ${clickable ? 'is-clickable' : ''}`
    const image = <SafeImage src={banner?.bannerImageUrl || ''} loading="lazy" decoding="async" draggable={false} />

    if (isExternalHttps) {
      return (
        <a key={key} className={className} href={bannerUrl} target="_blank" rel="noreferrer">
          {image}
        </a>
      )
    }

    if (clickable) {
      return (
        <button
          key={key}
          type="button"
          className={className}
          onClick={() => {
            if (isInternalPath) {
              history.push(bannerUrl, navigationState)
              return
            }

            window.location.href = bannerUrl
          }}>
          {image}
        </button>
      )
    }

    return (
      <div key={key} className={className}>
        {image}
      </div>
    )
  }

  return (
    <div className="HomeBannerCache">
      {showSkeleton ? (
        <>
          <div className="HomeBannerCache-slide">
            <div className="HomeBannerCache-item">
              <div className="HomeBannerCache-skeleton-block" />
            </div>
          </div>
          <div className="HomeBannerCache-dots">
            <div className="HomeBannerCache-skeleton-dot" />
          </div>
        </>
      ) : (
        <div className="HomeBannerCache-slider">
          <Slider ref={sliderRef} {...sliderSettings}>
            {sliderItems.map((banner, index) => (
              <div key={banner?.systemPromoBannersId ?? index} className="HomeBannerCache-slide">
                {renderBannerContent(banner, index)}
              </div>
            ))}
          </Slider>
        </div>
      )}
    </div>
  )
}
